import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Loader2, MoreHorizontal, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '@/hooks/use-toast';
import { usePermission } from '@/hooks/usePermission';
import { formatDate, formatShortDate } from '@/lib/formatDate';
import {
  listPatientDocuments,
  uploadPatientDocuments,
  deletePatientDocument,
  openPatientDocumentInline,
  fetchPatientDocumentBlobUrl,
  DocumentLink,
  PatientDocument,
} from '@/lib/patientDocumentsApi';
import DocumentUploadForm, { DocumentUploadMeta } from './DocumentUploadForm';
import TypeBadge from './TypeBadge';

interface PatientDocumentsProps {
  patientId: string;
  link: DocumentLink; // exactly one of prescriptionId / admissionId, or {} for patient-level
  onCountChange?: (count: number) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// document_date is a plain 'YYYY-MM-DD' from the API, but defend against a
// full ISO timestamp slipping through (e.g. from an older cached response).
function formatDocumentDate(value: string): string {
  return formatDate(value.length > 10 ? value.slice(0, 10) : value);
}

// Groups clinical photos by body_site so a doctor sees the before/after/
// follow-up timeline for that site together, chronologically. Non-photo
// documents (and photos without a body_site) fall into their own bucket.
function groupDocuments(docs: PatientDocument[]): { key: string; label: string; docs: PatientDocument[] }[] {
  const groups = new Map<string, PatientDocument[]>();
  for (const doc of docs) {
    const key = doc.document_type === 'clinical_photo' && doc.body_site ? `photo:${doc.body_site}` : `flat:${doc.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(doc);
  }
  return Array.from(groups.entries()).map(([key, groupDocs]) => ({
    key,
    label: key.startsWith('photo:') ? key.slice('photo:'.length) : '',
    docs: groupDocs,
  }));
}

// Fetches an authenticated blob for an image document and renders it as a
// thumbnail (the endpoint requires a Bearer token, so a plain <img src> to
// the API URL won't work). Owns and revokes its own object URL.
function PhotoThumbnail({ patientId, doc, index, onClick }: { patientId: string; doc: PatientDocument; index: number; onClick: () => void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let localUrl: string | null = null;
    fetchPatientDocumentBlobUrl(patientId, doc.id).then((u) => {
      if (cancelled) { URL.revokeObjectURL(u); return; }
      localUrl = u;
      setBlobUrl(u);
    }).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [patientId, doc.id]);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group/thumb relative w-[4.5rem] h-[4.5rem] rounded-md border border-border overflow-hidden bg-muted flex-shrink-0
                 shadow-sm transition-colors hover:border-primary/40"
      title={`${doc.file_name} — ${formatDocumentDate(doc.document_date)}`}
    >
      <span className="absolute top-1 left-1 z-10 w-4 h-4 rounded-full bg-black/55 backdrop-blur-sm text-white text-2xs font-semibold flex items-center justify-center tabular-nums">
        {index + 1}
      </span>
      {blobUrl && !failed ? (
        <img
          src={blobUrl}
          alt={doc.file_name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover/thumb:scale-105"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {failed
            ? <ImageIcon className="w-5 h-5 text-muted-foreground" />
            : <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
      )}
      <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 to-transparent pt-3 pb-1 px-1">
        <span className="block text-white text-2xs text-center truncate leading-none tabular-nums">
          {formatShortDate(doc.document_date)}
        </span>
      </span>
    </button>
  );
}

export default function PatientDocuments({ patientId, link, onCountChange }: PatientDocumentsProps) {
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  // Adding and removing need write access to what the document hangs on (the server checks the same)
  const { can } = usePermission();
  const canWrite = can(`${link.prescriptionId ? 'prescriptions' : link.admissionId ? 'ipd' : 'patients'}:write`);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listPatientDocuments(patientId, link);
      setDocuments(rows);
    } catch (err: any) {
      toast({ title: 'Failed to load documents', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [patientId, link.prescriptionId, link.admissionId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { onCountChange?.(documents.length); }, [documents, onCountChange]);

  const handleUpload = async (files: File[], meta: DocumentUploadMeta) => {
    setUploading(true);
    try {
      const result = await uploadPatientDocuments(patientId, { ...link, ...meta, files });

      if (result.created.length) {
        toast({ title: `Uploaded ${result.created.length} file${result.created.length > 1 ? 's' : ''}` });
        setAdding(false);
      }
      if (result.errors.length) {
        toast({
          title: `${result.errors.length} file(s) failed`,
          description: result.errors.map(e => `${e.file_name}: ${e.message}`).join('; '),
          variant: 'destructive',
        });
      }
      await refresh();
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc: PatientDocument) => {
    try {
      await deletePatientDocument(patientId, doc.id);
      toast({ title: 'Document removed' });
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (err: any) {
      toast({ title: 'Failed to remove document', description: err.message, variant: 'destructive' });
    }
  };

  const handleView = async (doc: PatientDocument) => {
    try {
      await openPatientDocumentInline(patientId, doc.id);
    } catch (err: any) {
      toast({ title: 'Failed to open document', description: err.message, variant: 'destructive' });
    }
  };

  const groups = groupDocuments(documents);
  // With nothing uploaded yet the form is the page; afterwards it opens from "Add document"
  const showForm = canWrite && (adding || (!loading && documents.length === 0));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 min-h-9">
        <h2 className="text-sm font-semibold text-foreground">
          Documents {!loading && <span className="ml-1 font-normal text-muted-foreground tabular-nums">{documents.length}</span>}
        </h2>
        {canWrite && documents.length > 0 && !adding && (
          <Button size="sm" onClick={() => setAdding(true)} className="gap-1.5">
            <Plus className="w-4 h-4" /> Add document
          </Button>
        )}
      </div>

      {showForm && (
        <DocumentUploadForm
          onSubmit={handleUpload}
          submitting={uploading}
          onCancel={documents.length > 0 ? () => setAdding(false) : undefined}
        />
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2 py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
        </div>
      ) : documents.length === 0 ? (
        !canWrite && (
          <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
            <p className="text-sm font-semibold text-foreground">No documents yet</p>
            <p className="text-sm text-muted-foreground mt-1">Uploaded reports, outside prescriptions and photos appear here.</p>
          </div>
        )
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden divide-y divide-divider animate-fade-in">
          {groups.map((group) => {
            // Clinical photos of one body site: a dated strip, so before / after read left to right
            if (group.label && group.docs.every(d => d.mime_type.startsWith('image/'))) {
              return (
                <div key={group.key} className="px-4 py-3">
                  <p className="text-[13px] font-medium text-foreground">
                    {group.label}
                    <span className="ml-1.5 font-normal text-muted-foreground">{group.docs.length} photo{group.docs.length > 1 ? 's' : ''}</span>
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {group.docs.map((doc, idx) => (
                      <div key={doc.id} className="relative">
                        <PhotoThumbnail patientId={patientId} doc={doc} index={idx} onClick={() => handleView(doc)} />
                        {canWrite && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label={`Remove photo ${idx + 1} of ${group.label}`}
                                className="touch-target absolute -top-1.5 -right-1.5 z-20 w-5 h-5 rounded-full border border-border bg-card text-muted-foreground shadow-sm flex items-center justify-center hover:text-destructive"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem onSelect={() => handleDelete(doc)} className="text-destructive focus:text-destructive">Remove photo</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return group.docs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 px-4 py-2 min-h-12 hover:bg-muted/50 transition-colors">
                {doc.mime_type.startsWith('image/')
                  ? <ImageIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                  : <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />}
                <button type="button" onClick={() => handleView(doc)} className="min-w-0 flex-1 text-left" title={`Open ${doc.file_name}`}>
                  <span className="block truncate text-sm font-medium text-foreground hover:underline underline-offset-2">{doc.file_name}</span>
                  <span className="block sm:hidden text-xs text-muted-foreground tabular-nums">
                    {formatDocumentDate(doc.document_date)} · {formatFileSize(doc.file_size)}
                  </span>
                </button>
                <TypeBadge type={doc.document_type} />
                <span className="hidden sm:block w-24 text-right text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">{formatDocumentDate(doc.document_date)}</span>
                <span className="hidden sm:block w-16 text-right text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">{formatFileSize(doc.file_size)}</span>
                {canWrite && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0 text-muted-foreground" aria-label={`More actions for ${doc.file_name}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onSelect={() => handleView(doc)}>Open</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => handleDelete(doc)} className="text-destructive focus:text-destructive">Remove document</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ));
          })}
        </div>
      )}
    </div>
  );
}
